import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { compact } from 'lodash-es';
import { pathgen } from 'object-path-generator';
import { Controller, useForm } from 'react-hook-form';
import { Button, Callout, ConfirmationModal, FunctionalLoader, MenuItem, SplitButton, TextField, useStateInRef, useDevice, Row, Badge, CircularLoader } from 'seagull';
import { ArchiveAdd, ArchiveLiner24, CopyLiner24, DocumentCheckmark24, EmailSent, LinkIcon, LinkLiner24, NoteTextLiner24, SendLiner24, TrashLiner24 } from 'seagull/icons';
import { generateUniqueId } from 'seagull/utils/generateUniqueId';
import { validateReviewers } from '~/business-logic/draftLogic';
import { templateNameValidation } from '~/business-logic/validators';
import { defaultAgreementTitle, routes } from '~/constants';
import { useEventReporter, useOverlayContext, useIoContext, useStore, useStoreActions } from '~/contexts';
import { useContactsMap } from '~/hooks/reactQuery/derived/useContactsMap';
import { useArchiveDraft } from '~/hooks/reactQuery/mutations/useArchiveDraft';
import { useCancelEditProposal } from '~/hooks/reactQuery/mutations/useCancelEditProposal';
import { useRepublishProposal } from '~/hooks/reactQuery/mutations/useRepublishProposal';
import { useCopyProposalLink } from '~/hooks/useCopyProposalLink';
import { useCreateRelationshipFromDraft } from '~/hooks/useCreateRelationshipFromDraft';
import { useOnMount } from '~/hooks/useOnMount';
import { useScheduledProposal } from '~/hooks/useScheduledProposal';
import { useSetWorksAtOptimisticUpdate } from '~/hooks/useSetWorksAtOptimisticUpdate';
import { useSignal } from '~/hooks/useSignal';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { ErrorResponse, ServerError } from '~/infra/ErrorResponse';
import { makeNetTerms } from '~/infra/translator';
import { handleRequest, notEmpty, sendErrReport, wait } from '~/infra/utils';
import { helpArticles } from '~/utils/helpArticles';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
import { ClientPreview } from '../../ClientPreview/ClientPreview';
import ConfirmExternallySignModal from '../../ConfirmExternallySignModal/ConfirmExternallySignModal';
import ExternalLinkButton from '../../ExternalLinkButton';
import { RelationshipEffectiveDate } from '../../RelationshipEffectiveDate/RelationshipEffectiveDate';
import { AgreementName } from '../AgreementName/AgreementName';
import AgreementStudioModal, { SubmitType } from '../AgreementStudioModal/AgreementStudioModal';
import styles from '../AgreementStudioPage.module.scss';
import { contactHasAgreement } from '../AgreementStudioPage.utils';
import { hasBilledImmediatelyService, makeDraft } from './AgreementStudioProposalMode.utils';
import { PostAgreementCreation } from './PostAgreementCreation';
import ReadyToSendModal from './ReadyToSendModal/ReadyToSendModal';
import { Recipients } from './Recipients/Recipients';
import ScheduleProposal from './ScheduleProposal/ScheduleProposal';
import { CopyLinksModal } from './SilentProposal/CopyLinksModal';
import { GenerateLinkModal } from './SilentProposal/GenerateLinkModal';
const createRelationshipLoader = 'createRelationship';
var DraftStatus = /*#__PURE__*/ function(DraftStatus) {
    DraftStatus["Loading"] = "loading";
    DraftStatus["Saved"] = "saved";
    DraftStatus["Error"] = "error";
    DraftStatus["None"] = "none";
    return DraftStatus;
}(DraftStatus || {});
function SaveAsTemplateModal(param) {
    let { draftId, dismissModal } = param;
    const { api } = useStoreActions();
    const { showSuccessToast, showErrorToast } = useOverlayContext();
    const { control, handleSubmit } = useForm({
        mode: 'all',
        defaultValues: {
            name: ''
        }
    });
    const [loading, setLoading] = useState(false);
    const closeModal = ()=>dismissModal(false);
    async function submit(param) {
        let { name } = param;
        setLoading(true);
        const result = await handleRequest(api.proposalTemplates.createFromDraft(draftId, name));
        if (result.success) {
            showSuccessToast('Proposal template successfully saved', 'proposal-template-saved-toast');
            dismissModal(true);
        } else {
            if (new ErrorResponse(result.error).hasServerError(ServerError.NameAlreadyExists)) {
                showErrorToast('A template with this name already exists', 'proposal-template-name-already-used');
            } else {
                showErrorToast('There was an error saving this template', 'proposal-template-not-saved-toast');
            }
        }
        setLoading(false);
    }
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        title: "Save as template",
        testId: "save-as-proposal-template-modal",
        content: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(Controller, {
                    name: "name",
                    control: control,
                    rules: templateNameValidation,
                    render: (p)=>/*#__PURE__*/ _jsx(TextField, _object_spread({
                            collapseHelperText: false,
                            autoFocus: true,
                            testId: "proposal-template-name",
                            label: "Template name"
                        }, connectControllerToTextField(p)))
                }),
                /*#__PURE__*/ _jsx(Callout, {
                    style: {
                        marginTop: '32px'
                    },
                    children: 'Your template can be managed and used through the "Proposal Templates" page.'
                })
            ]
        }),
        primaryButtonLabel: "Save",
        primaryButtonLoading: loading,
        onPrimaryButtonClick: handleSubmit(submit),
        secondaryButtonLabel: "Cancel",
        onSecondaryButtonClick: closeModal,
        onCloseButtonClick: closeModal
    });
}
function getSavingStatusMP(draftStatus) {
    const testId = "save-indicator-".concat(draftStatus);
    switch(draftStatus){
        case "none":
            return null;
        case "loading":
            return /*#__PURE__*/ _jsx(CircularLoader, {});
        case "saved":
            return /*#__PURE__*/ _jsx(Badge, {
                skin: "success",
                rounded: true,
                outlined: true,
                testId: testId,
                children: "Draft saved"
            });
        case "error":
            return /*#__PURE__*/ _jsx(Badge, {
                skin: "error",
                rounded: true,
                outlined: true,
                testId: testId,
                children: "Draft save error"
            });
    }
}
export const AgreementStudioProposalModeTestIds = pathgen('AgreementStudioProposalModeTestIds');
export function AgreementStudioProposalMode(props) {
    var _ref, _ref1, _ref2;
    const { isMobileDevice } = useDevice();
    const { viewProposalMode } = props;
    const { archive } = useArchiveDraft({
        withPageLoader: true,
        onSuccess: ()=>history.push(routes.draftsDashboard),
        biCode: 18801
    });
    const isViewProposal = !!viewProposalMode;
    const draft = viewProposalMode ? viewProposalMode.proposalAsDraft : props.draft;
    const { reportToAppcues, reportToGtm, reportToHubspot, reportToHeap, prepareFormObject, reportProductsFormSubmission, reportButtonClick } = useEventReporter();
    const history = useHistory();
    const { api, logBI } = useStoreActions();
    const overlayContext = useOverlayContext();
    const { pushBottomSheetPage, popBottomSheetPage } = overlayContext;
    const legalTermsDocuments = useStore((state)=>state.legalTerms.data);
    const agreements = useStore((state)=>state.agreements.data);
    const proposals = useStore((state)=>state.proposals.data);
    const createRelationshipFromDraft = useCreateRelationshipFromDraft();
    const updateWorksAt = useSetWorksAtOptimisticUpdate();
    const scrollToTopRef = useRef(null);
    const [newContactValid, setNewContactValid] = useState(true);
    const [titleValid, setTitleValid] = useState((draft === null || draft === void 0 ? void 0 : draft.title()) != null);
    const [clientContactId, setClientContactId] = useState(draft === null || draft === void 0 ? void 0 : draft.clientContactId());
    const [showAgreementTitleWarning, setShowAgreementTitleWarning] = useState(false);
    const locationState = useLocation().state;
    const [isSampleRelationship, setIsSampleRelationship] = useState((_ref = (_ref1 = draft === null || draft === void 0 ? void 0 : draft.isSample()) !== null && _ref1 !== void 0 ? _ref1 : locationState === null || locationState === void 0 ? void 0 : locationState.defaultToSampleContact) !== null && _ref !== void 0 ? _ref : false);
    const [effectiveDate, setEffectiveDate] = useState(()=>(draft === null || draft === void 0 ? void 0 : draft.effectiveDateAsDate()) || null);
    const [showCustomerDetailsWarning, setShowCustomerDetailsWarning] = useState(false);
    const [draftId, setDraftId] = useState(draft === null || draft === void 0 ? void 0 : draft.id());
    const getDraftId = useStateInRef(draftId);
    const [reviewers, setReviewers] = useState(draft ? draft.reviewers() : []);
    const [showReviewerErrors, signalShowReviewerErrors] = useSignal();
    const { getNow } = useIoContext();
    const { contactsMap, sampleContact, fetchContacts } = useContactsMap(props.worksAt.id);
    const { showSwitchToEditModal } = useScheduledProposal({
        draftId: draftId,
        isSampleRelationship,
        refresh: ()=>{},
        onCloseCancelSchedulingModal: ()=>history.push(routes.draftsDashboard)
    });
    const isSavingDraft = useRef(false);
    const [draftStatus, setDraftStatus] = useState("none");
    const existingContact = clientContactId ? contactsMap[clientContactId] : isSampleRelationship ? sampleContact : undefined;
    const [agreementTitle, setAgreementTitle] = useState((draft === null || draft === void 0 ? void 0 : draft.title()) || defaultAgreementTitle);
    useOnMount(()=>{
        if (draft === null || draft === void 0 ? void 0 : draft.isScheduled()) {
            showSwitchToEditModal();
        }
    });
    const showAgreementCreationToast = ()=>overlayContext.showSuccessToast('Agreement was sent successfully');
    const navigationAfterCreation = (redirectUrl)=>{
        const url = redirectUrl || routes.proposalsDashboard;
        if (url === routes.agreementStudio) {
            history.push(url, {
                fromPostAgreement: true
            });
            history.go(0);
        } else {
            history.push(url);
        }
    };
    const { getAgreementNotificationLinks } = useCopyProposalLink();
    const draftToCommonWithId = (draft)=>{
        const bundles = draft.serviceBundles();
        return {
            id: generateUniqueId('proposal-template'),
            coverLetter: draft.coverLetter(),
            extraClause: draft.extraClause(),
            requirePaymentMethod: draft.requirePaymentMethod(),
            allowManualPayments: draft.allowManualPayments(),
            isExistingCustomer: draft.isExistingCustomer(),
            customNotificationText: draft.customNotificationText(),
            services: draft.services(),
            serviceBundles: bundles.length > 0 ? {
                bundles
            } : undefined,
            netTerms: draft.netTerms(),
            legalTermsDocuments: draft.legalTermsDocuments().filter(notEmpty),
            coverCreditCardFees: draft.coverCreditCardFees(),
            /** Will automatic ongoing services be billed at the start or end of the billing-period. */ amendmentsApprovalMethod: draft.amendmentsApprovalMethod(),
            invoicesApprovalMethod: draft.invoicesApprovalMethod(),
            requestAccountantAccess: draft.requestAccountantAccess(),
            introVideo: draft.introVideo(),
            expirationDate: draft.expirationDate()
        };
    };
    const linkedRelationshipId = (_ref2 = draft === null || draft === void 0 ? void 0 : draft.linkedRelationshipId()) !== null && _ref2 !== void 0 ? _ref2 : null;
    const { t: editInPlaceTexts } = useLocaleKeys('agreementStudio.editInPlace');
    const { republishProposal } = useRepublishProposal({
        onSuccess: ()=>history.push(routes.proposalsDashboard)
    });
    const { cancelEditProposal } = useCancelEditProposal({
        onSuccess: ()=>history.push(routes.proposalsDashboard)
    });
    const discardDraft = async ()=>{
        const loaderName = 'discardDraft';
        overlayContext.startLoader(loaderName);
        try {
            await api.draft.deleteById(draftId);
            history.push(routes.draftsDashboard);
        } finally{
            overlayContext.stopLoader(loaderName);
        }
    };
    const genDraftId = ()=>{
        if (draftId) {
            return draftId;
        } else {
            const id = generateUniqueId('draft');
            setDraftId(id);
            window.history.replaceState('', 'Draft', routes.draft(id));
            return id;
        }
    };
    const getDraft = (data, titleOverride)=>{
        return makeDraft(_object_spread_props(_object_spread({}, data), {
            id: genDraftId(),
            title: titleOverride !== null && titleOverride !== void 0 ? titleOverride : agreementTitle,
            clientContactId,
            isSample: isSampleRelationship,
            effectiveDate,
            reviewers,
            metadata: draft === null || draft === void 0 ? void 0 : draft.metadata(),
            linkedRelationshipId
        }));
    };
    const saveDraft = async (data, isMounted)=>{
        if (!isMounted()) return;
        isSavingDraft.current = true;
        setDraftStatus("loading");
        const draftToSave = getDraft(data);
        const result = await handleRequest(api.draft.save(draftToSave));
        if (!isMounted()) return;
        isSavingDraft.current = false;
        setDraftStatus(result.success ? "saved" : "error");
    };
    async function submit(data, submitType) {
        var _props_worksAt_key;
        if (linkedRelationshipId) {
            while(isSavingDraft.current){
                await wait(300);
            }
            republishProposal({
                draft: getDraft(data),
                relationshipId: linkedRelationshipId
            });
            return;
        }
        const sendSilently = submitType === SubmitType.SendSilently;
        const externallySign = submitType === SubmitType.ExternallySign;
        const firstName = existingContact ? existingContact.firstName() : '';
        const lastName = existingContact ? existingContact.lastName() : '';
        const companyName = existingContact ? existingContact.companyName() : '';
        const email = existingContact ? existingContact.email() : '';
        reportButtonClick('agreement-studio-topbar-btn-submit');
        logBI(10485);
        if (!newContactValid || !firstName || firstName.length < 2 || !lastName || lastName.length < 2 || !companyName) {
            if (scrollToTopRef.current != null) scrollToTopRef.current();
            setShowCustomerDetailsWarning(true);
            return;
        }
        if (!validateReviewers(reviewers, existingContact)) {
            signalShowReviewerErrors(true);
            return;
        }
        if (!titleValid) {
            setShowAgreementTitleWarning(true);
            return;
        }
        if (submitType === SubmitType.ExternallySign && isSampleRelationship) {
            overlayContext.showErrorToast('You cannot "approve on behalf of your client" for a sample contact', 'externally-sign-sample-agreement');
            return;
        }
        const confirmed = await overlayContext.showModal({
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                switch(submitType){
                    case SubmitType.ExternallySign:
                        return /*#__PURE__*/ _jsx(ConfirmExternallySignModal, {
                            hasBilledImmediatelyService: hasBilledImmediatelyService(data.services, effectiveDate, getNow()),
                            dismissModal: dismissModal
                        });
                    case SubmitType.SendSilently:
                        return /*#__PURE__*/ _jsx(GenerateLinkModal, {
                            recipientsCount: reviewers.length + compact([
                                existingContact
                            ]).length,
                            dismissModal: dismissModal
                        });
                    case SubmitType.SendProposal:
                        return /*#__PURE__*/ _jsx(ReadyToSendModal, {
                            firstName: firstName,
                            lastName: lastName,
                            companyName: companyName,
                            email: email,
                            reviewers: reviewers,
                            dismissModal: dismissModal
                        });
                    case SubmitType.ScheduleProposal:
                        return /*#__PURE__*/ _jsx(ScheduleProposal, {
                            dismissModal: dismissModal,
                            draftId: draftId
                        });
                }
            }
        });
        if (!confirmed) return;
        if (isSampleRelationship && submitType !== SubmitType.ScheduleProposal) {
            logBI(11901);
            reportToGtm('form-success', 'vendor-sent-test-agreement');
            reportToAppcues('form-success', 'vendor-sent-test-agreement');
            reportToHeap('testAgreement', 'submit', 'vendor-sent-test-agreement');
            reportToHubspot('vendor_sent_a_test_agreement');
        } else if (submitType === SubmitType.SendProposal) {
            logBI(10479, {
                clientEmail: email,
                selectedDay: undefined
            });
            reportProductsFormSubmission('send-agreement-for-approval', [
                prepareFormObject('buyerName', {
                    first: firstName,
                    last: lastName
                }),
                prepareFormObject('buyerEmail', email),
                prepareFormObject('customerName', companyName),
                prepareFormObject('serviceCount', data.services.length),
                prepareFormObject('netTerms', makeNetTerms(data.netTerms)),
                prepareFormObject('isExistingCustomer', data.isExistingCustomer)
            ]);
        }
        overlayContext.startLoader(createRelationshipLoader);
        // To avoid a race-condition, don't allow to create a relationship while we're saving a draft
        while(isSavingDraft.current){
            await wait(300);
        }
        if (submitType === SubmitType.ScheduleProposal) {
            overlayContext.stopLoader(createRelationshipLoader);
            navigationAfterCreation(routes.draftsDashboard);
            return;
        }
        const result = await handleRequest(createRelationshipFromDraft(getDraftId(), {
            externallySign,
            sendSilently,
            isSampleRelationship
        }));
        if (!result.success) {
            const { text } = result.error.createRelationshipErrorText();
            overlayContext.showErrorToast(text, 'create-relationship-error');
            saveDraft(data, ()=>true);
            overlayContext.stopLoader(createRelationshipLoader);
            return;
        }
        const key = isSampleRelationship ? 'numSampleAgreements' : 'numSaleAgreements';
        const { pending, approved } = (_props_worksAt_key = props.worksAt[key]) !== null && _props_worksAt_key !== void 0 ? _props_worksAt_key : {
            pending: 0,
            approved: 0
        };
        updateWorksAt(isSampleRelationship, externallySign);
        if (!isSampleRelationship && pending === 0 && approved === 0) {
            // Important note: this event should be sent from the backend, as we can't assure that the "worksAt" is correct.
            // It is a technical gap.
            reportToGtm('form-success', 'user-successfully-sent-first-agreement');
            reportToAppcues('form-success', 'user-successfully-sent-first-agreement');
            reportToHubspot('user_successfully_sent_first_agreement_');
            reportToHeap('agreementStudio', 'submit', 'user-successfully-sent-first-agreement');
        }
        overlayContext.stopLoader(createRelationshipLoader);
        switch(submitType){
            case SubmitType.ExternallySign:
                overlayContext.showSuccessToast('Agreement was approved successfully', 'externally-sign-success');
                navigationAfterCreation(routes.agreementsDashboard);
                break;
            case SubmitType.SendSilently:
                try {
                    const recipients = await getAgreementNotificationLinks(result.data);
                    await overlayContext.showModal({
                        closeOnClickOutside: true,
                        closeOnEscapeKey: true,
                        content: (param)=>{
                            let { dismissModal } = param;
                            return /*#__PURE__*/ _jsx(CopyLinksModal, {
                                dismissModal: dismissModal,
                                recipients: recipients
                            });
                        }
                    });
                    navigationAfterCreation(routes.proposalsDashboard);
                } catch (error) {}
                break;
            case SubmitType.SendProposal:
                try {
                    const notification = await getAgreementNotificationLinks(result.data);
                    const redirectUrl = await overlayContext.showModal({
                        closeOnClickOutside: true,
                        closeOnEscapeKey: true,
                        content: (param)=>{
                            let { dismissModal } = param;
                            return /*#__PURE__*/ _jsx(PostAgreementCreation, {
                                testId: "agreement-studio-post-creation-modal",
                                onClose: dismissModal,
                                name: "".concat(firstName, " ").concat(lastName),
                                sampleAgreementData: isSampleRelationship ? {
                                    email: props.userEmail
                                } : undefined,
                                proposalLinks: notification
                            });
                        }
                    });
                    navigationAfterCreation(redirectUrl);
                } catch (error) {
                    sendErrReport('failed to show success modal', error);
                    showAgreementCreationToast(); //fail silently in a reasonable way
                    navigationAfterCreation();
                }
                break;
        }
    }
    const openPreviewModal = (data, resolvedTitle)=>{
        reportButtonClick('agreement-previewed-agreement');
        if (!legalTermsDocuments) return;
        const draft = getDraft(data, resolvedTitle);
        overlayContext.showModal({
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (props)=>/*#__PURE__*/ _jsx(ClientPreview, _object_spread({
                    type: "draft",
                    draftInfo: {
                        draft,
                        sampleContact,
                        contactsMap
                    }
                }, props))
        });
    };
    async function saveAsTemplate() {
        if (!draftId) return;
        const success = await overlayContext.showModal({
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(SaveAsTemplateModal, {
                    draftId: draftId,
                    dismissModal: dismissModal
                });
            }
        });
        if (success) history.push(routes.proposalTemplates);
    }
    const getInlineFooterActions = ()=>isViewProposal ? [
            _object_spread_props(_object_spread({}, viewProposalMode.sendReminder), {
                testId: 'agreement-studio-send-reminder',
                iconStart: /*#__PURE__*/ _jsx(EmailSent, {})
            }),
            _object_spread_props(_object_spread({}, viewProposalMode.copyLink), {
                testId: 'agreement-studio-copy-link',
                iconStart: /*#__PURE__*/ _jsx(LinkIcon, {})
            })
        ] : [
            {
                text: 'Save as template',
                intent: Intent.CreateProposalTemplate,
                testId: 'agreement-studio-save-template',
                disabled: draftId == null,
                onClick: saveAsTemplate,
                tooltipTitle: draftId == null ? 'Make changes to your draft in order to save it as a proposal template' : undefined,
                iconStart: /*#__PURE__*/ _jsx(ArchiveAdd, {})
            }
        ];
    const renderSubmitMenuItem = (param)=>{
        let { text, icon, testId, intent, onClick } = param;
        return /*#__PURE__*/ _jsx(MenuItem, {
            testId: testId,
            intent: intent,
            onClick: onClick,
            children: /*#__PURE__*/ _jsxs(Row, {
                gap: 12,
                alignItems: "center",
                children: [
                    icon,
                    /*#__PURE__*/ _jsx("span", {
                        children: text
                    })
                ]
            })
        }, testId);
    };
    return legalTermsDocuments && contactsMap ? /*#__PURE__*/ _jsx(AgreementStudioModal, _object_spread_props(_object_spread({}, props), {
        draft: draft != null ? draftToCommonWithId(draft) : undefined,
        legalTermsSourceId: draftId,
        isSampleRelationship: isSampleRelationship,
        isReadOnly: isViewProposal,
        modalTitle: isViewProposal ? 'View proposal' : 'Create proposal',
        modalTitleTestId: isViewProposal ? 'view-proposal-title' : 'create-proposal-title',
        contact: existingContact,
        agreementTitle: agreementTitle,
        legalTermsDocuments: legalTermsDocuments,
        effectiveDate: effectiveDate,
        additionalAutoSaveDependencies: [
            agreementTitle,
            clientContactId,
            effectiveDate,
            reviewers
        ],
        doScrollToTopRef: scrollToTopRef,
        savingStatusMP: isMobileDevice ? null : getSavingStatusMP(draftStatus),
        agreementTitleMP: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(AgreementName, {
                    fullWidth: isMobileDevice,
                    agreementTitle: agreementTitle,
                    setAgreementTitle: setAgreementTitle,
                    setAgreementTitleValid: setTitleValid,
                    showWarning: showAgreementTitleWarning,
                    label: "Agreement name",
                    disabled: isViewProposal,
                    testId: "agreement-studio-customer-details-input-display-title"
                }),
                /*#__PURE__*/ _jsx(Recipients, {
                    contactDetailsProps: {
                        existingContact,
                        showCustomerDetailsWarning,
                        disabled: isViewProposal,
                        refreshExistingContacts: fetchContacts,
                        onValidation: setNewContactValid,
                        onChange: (change)=>{
                            setIsSampleRelationship(change.type === 'sampleContact');
                            setClientContactId(change.type === 'existingContact' ? change.id : null);
                            if (change.type === 'existingContact') {
                                setShowCustomerDetailsWarning(false);
                            }
                        }
                    },
                    reviewers: reviewers,
                    showReviewerErrors: showReviewerErrors,
                    isSampleRelationship: isSampleRelationship,
                    onReviewersChange: setReviewers,
                    amendmentInfoCallout: existingContact && !isViewProposal && contactHasAgreement(existingContact, agreements, proposals) && /*#__PURE__*/ _jsxs(Callout, {
                        children: [
                            "It appears you have an agreement with ",
                            existingContact.companyName(),
                            ". To change terms, or add/edit services, edit the existing agreement instead of creating a new one.",
                            ' ',
                            /*#__PURE__*/ _jsx(ExternalLinkButton, {
                                openInNewTab: true,
                                href: helpArticles.howToEditAgreement(),
                                testId: "agreement-studio-amendment-info",
                                children: "Learn more"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(RelationshipEffectiveDate, {
                    value: effectiveDate,
                    disabled: isViewProposal,
                    onChange: setEffectiveDate,
                    title: "Agreement effective date",
                    description: "The date when you can start providing the services and bill the client."
                })
            ]
        }),
        inlineFooterActions: getInlineFooterActions(),
        additionalFooterActions: isViewProposal ? [
            _object_spread_props(_object_spread({}, viewProposalMode.duplicate), {
                iconStart: /*#__PURE__*/ _jsx(CopyLiner24, {}),
                testId: 'agreement-studio-duplicate-proposal'
            }),
            _object_spread_props(_object_spread({}, viewProposalMode.cancel), {
                iconStart: /*#__PURE__*/ _jsx(TrashLiner24, {}),
                skin: 'error',
                testId: 'agreement-studio-cancel-proposal'
            })
        ] : compact([
            !linkedRelationshipId && {
                text: 'Archive draft',
                disabled: draftId == null || (draft === null || draft === void 0 ? void 0 : draft.scheduledDate()) != null,
                iconStart: /*#__PURE__*/ _jsx(ArchiveLiner24, {}),
                testId: AgreementStudioProposalModeTestIds.ArchiveDraft(),
                onClick: ()=>{
                    archive(draftId);
                }
            },
            !linkedRelationshipId && {
                text: 'Discard draft',
                skin: 'error',
                disabled: draftId == null,
                iconStart: /*#__PURE__*/ _jsx(TrashLiner24, {}),
                testId: 'agreement-studio-topbar-discard-draft',
                onClick: discardDraft
            },
            linkedRelationshipId && {
                text: editInPlaceTexts.cancelEditFooterAction(),
                skin: 'error',
                iconStart: /*#__PURE__*/ _jsx(TrashLiner24, {}),
                testId: AgreementStudioProposalModeTestIds.CancelEdit(),
                onClick: ()=>cancelEditProposal(linkedRelationshipId)
            }
        ]),
        onSubmitCb: submit,
        onAutoSaveCb: isViewProposal ? undefined : saveDraft,
        openPreviewModal: openPreviewModal,
        renderSubmitButton: (param)=>{
            let { testId, className, onSubmit } = param;
            if (isViewProposal) {
                const { text, intent, disabled, onClick } = viewProposalMode.edit;
                return /*#__PURE__*/ _jsx(Button, {
                    intent: intent,
                    testId: testId,
                    className: className,
                    disabled: disabled,
                    size: isMobileDevice ? 'small' : 'medium',
                    onClick: onClick,
                    children: text
                });
            } else if (linkedRelationshipId) {
                return /*#__PURE__*/ _jsx(Button, {
                    intent: Intent.SendProposal,
                    testId: AgreementStudioProposalModeTestIds.RepublishProposal(),
                    className: className,
                    size: isMobileDevice ? 'small' : 'medium',
                    onClick: ()=>onSubmit(SubmitType.SendProposal),
                    children: editInPlaceTexts.republishButton()
                });
            } else {
                const submitMenuItems = [
                    {
                        testId: 'send-proposal',
                        icon: /*#__PURE__*/ _jsx(SendLiner24, {}),
                        text: 'Send now by email',
                        intent: Intent.SendProposal,
                        onClick: ()=>onSubmit(SubmitType.SendProposal)
                    },
                    {
                        testId: 'silent-proposal',
                        icon: /*#__PURE__*/ _jsx(LinkLiner24, {}),
                        text: 'Share by link',
                        onClick: ()=>onSubmit(SubmitType.SendSilently)
                    },
                    {
                        testId: 'schedule-proposal',
                        icon: /*#__PURE__*/ _jsx(NoteTextLiner24, {}),
                        text: 'Schedule send',
                        onClick: ()=>onSubmit(SubmitType.ScheduleProposal)
                    },
                    {
                        testId: 'sign-externally',
                        icon: /*#__PURE__*/ _jsx(DocumentCheckmark24, {}),
                        text: 'Approve on behalf of your client',
                        onClick: ()=>onSubmit(SubmitType.ExternallySign)
                    }
                ];
                return isMobileDevice ? /*#__PURE__*/ _jsx(Button, {
                    intent: Intent.SendProposal,
                    testId: testId,
                    className: className,
                    size: "iconOnly",
                    onClick: ()=>{
                        pushBottomSheetPage({
                            content: /*#__PURE__*/ _jsx(_Fragment, {
                                children: submitMenuItems.map((item)=>renderSubmitMenuItem(_object_spread_props(_object_spread({}, item), {
                                        onClick: ()=>{
                                            popBottomSheetPage();
                                            item.onClick();
                                        }
                                    })))
                            })
                        });
                    },
                    children: /*#__PURE__*/ _jsx(SendLiner24, {
                        size: 16
                    })
                }) : /*#__PURE__*/ _jsx(SplitButton, {
                    label: "Send",
                    testId: testId,
                    className: className,
                    placement: "bottom-end",
                    intent: Intent.SendProposal,
                    onClick: ()=>onSubmit(SubmitType.SendProposal),
                    popperClassName: styles.popper,
                    children: submitMenuItems.map((item)=>renderSubmitMenuItem(item))
                });
            }
        }
    })) : /*#__PURE__*/ _jsx(FunctionalLoader, {
        show: true
    });
}
